// @ts-strict-ignore
import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Upload } from '@node-space/ui-components'
import { DocumentUpload } from 'types/verification'
import {
  convertDataUrlToArrayBuffer,
  dropZoneAcceptedDocuments,
  FILE_UPLOAD_LIMIT,
  getMimeTypeFromArrayBuffer,
} from 'utils/documents'
import { logError } from 'utils/sentry'

interface UploadFileProps {
  file: DocumentUpload
  setFile: (fileData: DocumentUpload) => void
}

export default function UploadCaseDocument({ file, setFile }: UploadFileProps) {
  const { t } = useTranslation()
  const [error, setError] = useState<string>('')

  const onFileChange = useCallback(
    (acceptedFile: File | null) => {
      // -- clear state
      setError('')
      setFile(null)

      // Handle file removal - when acceptedFile is null, clear the state
      if (!acceptedFile) {
        setFile(null)
        return
      }

      try {
        const fileExtension: string = acceptedFile?.type
        const fileExtensionDisplay: string = fileExtension.split('/')[1].toLocaleUpperCase()

        // -- error: file exceeds max size
        if (acceptedFile?.size > FILE_UPLOAD_LIMIT) {
          setError(t('documents.smallerFileRequired'))
          return
        }

        // -- read file data and set state
        const reader = new FileReader()

        // -- to handle other events on the reader, use 'reader.onabort' and 'reader.onerror'
        reader.onload = () => {
          const dataUrl = reader?.result as string

          const arrayBuffer: ArrayBuffer = convertDataUrlToArrayBuffer(dataUrl)
          const mimeType: string = getMimeTypeFromArrayBuffer(arrayBuffer)

          // -- error: file extension and the mime type is incompatible
          if (!mimeType || mimeType !== fileExtension) {
            setError(t('documents.mimeTypeError', { extension: fileExtensionDisplay }))
            return
          }

          const fileData = { fileData: acceptedFile, buffer: dataUrl }
          setFile && setFile(fileData)
        }

        reader.readAsDataURL(acceptedFile)
      } catch (error) {
        logError('uploadFileOnDrop', error)
        setError(t('oopsSomethingWentWrong'))
      }
    },
    [setFile, setError, t]
  )

  return (
    <Upload
      full
      onFileChange={onFileChange}
      textDictionary={{
        defaultText: t('documents.defaultMessage'),
        dragAndDrop: t('documents.dropFile'),
        uploadText: t('documents.uploadFile'),
        replaceText: t('documents.replaceFile'),
      }}
      errorMessage={error}
      label={t('documents.uploadADocument')}
      accept={dropZoneAcceptedDocuments}
      testId="case-document"
    />
  )
}
